import React, { useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import { useRecoilState } from 'recoil';
import ModalPetInfo from '../../Web/Common/ModalPetInfo';
import { appointmentStatusesArchiveModalStateAtom } from './AppointmentStatusesArchive.state';
import { useGetAppointmentStatusHistory } from '../../../hooks/appointments/useGetAppointmentHistory';
import { FooterButtons } from '../../../shared/components/Modals/FooterButtons';
import { COLUMN_TYPES, PHASES } from '../../../config/app.config';
import { DataTable } from '../ModalTables';
import { ImageGalleryModal, TextDisplayModal } from '../ContentModals';
import { useNestedModal } from './AppointmentStatusesArchive.hooks';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import {
  buildStatusUpdateArchiveTranscript,
  buildStatusUpdateArchiveHeader,
} from '../../../utils/buildStatusUpdateArchiveTranscript';

const getTableColumns = (messageModal, imagesModal) => [
  {
    header: 'Date',
    field: 'dateTime',
    type: COLUMN_TYPES.DATE,
    headerClassName: 'date-column',
  },
  {
    header: 'Time',
    field: 'dateTime',
    type: COLUMN_TYPES.TIME,
    headerClassName: 'time-column',
  },
  {
    header: 'Phase',
    field: 'phaseId',
    type: COLUMN_TYPES.CUSTOM,
    headerClassName: 'phase-column',
    renderCell: (phaseId) => (phaseId ? PHASES[phaseId] : '-'),
  },
  {
    header: 'Updates',
    field: 'message',
    type: COLUMN_TYPES.TRUNCATED_TEXT,
    headerClassName: 'stretched-column',
    maxLines: 10,
    ellipsisRenderer: (message, rowData) => (
      <span>
        ...{' '}
        <button
          type="button"
          className="nav-link-style"
          onClick={() => messageModal.open(rowData)}
        >
          View
        </button>
      </span>
    ),
  },
  {
    header: 'Images',
    field: 'gallery',
    type: COLUMN_TYPES.IMAGES,
    headerClassName: 'action-column',
    onImageClick: (images) => imagesModal.open(images),
    buttonText: (count) => `View (${count})`,
  },
];

export function AppointmentStatusesArchiveModal() {
  const [modalState, setModalState] = useRecoilState(
    appointmentStatusesArchiveModalStateAtom,
  );
  const [copiedArchive, setCopiedArchive] = useState(false);

  const messageModal = useNestedModal();
  const imagesModal = useNestedModal();

  const { data: services } = useGetFacilityServices();
  const isBoardingOnly = !!services?.isBoardingOnly;
  const labels = useAppointmentLabels();

  const { data } = useGetAppointmentStatusHistory(
    { appointmentId: modalState.appointmentId },
    {
      enabled: !!modalState.open && !!modalState.appointmentId,
    },
  );

  const messages = useMemo(
    () => data?.messages || [],
    [data?.messages],
  );

  const allImages = useMemo(
    () =>
      messages.flatMap((row) =>
        Array.isArray(row.gallery) ? row.gallery : [],
      ),
    [messages],
  );

  if (!modalState.open) return null;

  const tableColumns = getTableColumns(messageModal, imagesModal);

  const handleCopyArchive = () => {
    const baseHeader = buildStatusUpdateArchiveHeader(data);
    const text = buildStatusUpdateArchiveTranscript({
      appointmentType:
        modalState.typeTitle ||
        data?.typeTitle ||
        data?.typeOther ||
        '',
      header: {
        ...baseHeader,
        doctor: isBoardingOnly
          ? ''
          : modalState.doctor || baseHeader.doctor,
      },
      messages,
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopiedArchive(true);
      setTimeout(() => setCopiedArchive(false), 1500);
    });
  };

  return (
    <>
      <Modal
        isOpen={modalState.open}
        size="lg"
        keyboard
        toggle={() => setModalState({ open: false })}
        className="model-form appointment-status-update-archive"
      >
        <ModalHeader className="pet-summary-wrapper">
          <ModalPetInfo
            petSummary={data?.petSummary ?? {}}
            avatar={data?.petSummary?.avatar}
            title={
              isBoardingOnly ? 'Photo\nUpdate History' : labels.appointments
            }
          />
        </ModalHeader>
        <ModalBody>
          <div className="modal-table">
            <div className="status-archive-header-row">
              <div className="status-archive-header-title">
                <h3 className="large">{labels.appointments}</h3>
                <Button
                  type="button"
                  className="btn-utility"
                  onClick={handleCopyArchive}
                  disabled={messages.length === 0}
                >
                  <ContentCopyIcon size={16} aria-hidden />
                  {labels.copyHistory}
                </Button>
                {copiedArchive && (
                  <span className="copied-hint">Copied!</span>
                )}
              </div>
              <div className="status-archive-all-images">
                {allImages.length > 0 && (
                  <button
                    type="button"
                    className="nav-link-style history-attachment-link"
                    onClick={() => imagesModal.open(allImages)}
                  >
                    {`All images (${allImages.length})`}
                  </button>
                )}
              </div>
            </div>
            <div className="status-archive-container">
              <DataTable
                columns={tableColumns}
                rows={messages}
                noDataMessage="No status history available for this appointment."
                noDataColSpan={5}
              />
            </div>
          </div>

          <FooterButtons
            onClose={() => setModalState({ open: false })}
            showSaveButton={false}
          />
        </ModalBody>
      </Modal>

      <TextDisplayModal
        isOpen={messageModal.isOpen}
        onClose={messageModal.close}
        title="Update"
        content={messageModal?.record?.message || ''}
      />

      <ImageGalleryModal
        isOpen={imagesModal.isOpen}
        onClose={imagesModal.close}
        title="Image Viewer"
        images={
          Array.isArray(imagesModal?.record)
            ? imagesModal.record
            : imagesModal?.record?.gallery || []
        }
        zipName="status-update-images.zip"
      />
    </>
  );
}
